import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, ScanBarcode, Layers, CheckCircle2, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

/**
 * GuidedWalkthrough - Interactive onboarding overlay for first-time retailers
 * 
 * Walks users through the scan-to-post flow with animated tooltips:
 * 1. Welcome - Introduction to the scanner
 * 2. Scan Input - How to enter/scan a barcode
 * 3. Add to Queue - Building your batch
 * 4. Review & Post - Confirming deals
 * 5. Success - Celebration
 */

const WALKTHROUGH_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to DealShaq Scanner! 🎉',
    description: 'Let\'s walk you through posting your first sizzling hot deals. It only takes a few minutes!',
    target: null, // No specific target - modal overlay
    position: 'center',
    icon: Sparkles,
  },
  {
    id: 'scan-input',
    title: 'Enter a Barcode',
    description: 'Type or scan a product barcode here. You can use a USB barcode scanner for fastest entry, or type the code manually.',
    target: '[data-testid="barcode-input"]',
    position: 'bottom',
    icon: ScanBarcode,
    highlight: true,
  },
  {
    id: 'scan-button',
    title: 'Look Up Product',
    description: 'Click "Scan" to look up the product info. We\'ll automatically find details from our database.',
    target: '[data-testid="scan-button"]',
    position: 'bottom',
    icon: ScanBarcode,
    highlight: true,
  },
  {
    id: 'add-queue',
    title: 'Build Your Queue',
    description: 'Add products to your queue. You need at least 2 items to create a deal batch. This helps ensure variety for shoppers!',
    target: '[data-testid="add-to-queue"]',
    position: 'top',
    icon: Layers,
    highlight: true,
    waitForElement: true,
  },
  {
    id: 'review-post',
    title: 'Review & Post',
    description: 'Once you have 2+ items, click "Review & Post" to set prices and publish your deals. Shoppers in your area will be notified!',
    target: '[data-testid="review-queue-btn"]',
    position: 'bottom',
    icon: CheckCircle2,
    highlight: true,
    waitForElement: true,
  },
  {
    id: 'success',
    title: 'You\'re All Set! 🎊',
    description: 'That\'s it! Your deals will be visible to local shoppers immediately. They\'ll receive notifications if your products match their favorites.',
    target: null,
    position: 'center',
    icon: PartyPopper,
  },
];

const GuidedWalkthrough = ({ 
  isOpen, 
  onClose, 
  onComplete,
  startAtStep = 0 
}) => {
  const [currentStep, setCurrentStep] = useState(startAtStep);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [targetElement, setTargetElement] = useState(null);
  
  // Reset to first step whenever walkthrough is opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);
  
  const step = WALKTHROUGH_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WALKTHROUGH_STEPS.length - 1;
  const StepIcon = step.icon;
  
  // Position the tooltip relative to the target element
  const updateTooltipPosition = useCallback(() => {
    if (!step.target) {
      setTargetElement(null);
      setTooltipPosition({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    
    const element = document.querySelector(step.target);
    if (element) {
      setTargetElement(element);
      const rect = element.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
      
      // Tooltip dimensions (approximate)
      const tooltipWidth = 340;
      const tooltipHeight = 260;
      const margin = 16; // Gap between tooltip and target
      
      let top, left, transform;
      
      switch (step.position) {
        case 'top':
          // Position above the element
          top = rect.top + scrollTop - tooltipHeight - margin;
          left = rect.left + scrollLeft + rect.width / 2;
          transform = 'translateX(-50%)';
          
          // If tooltip would go above viewport, flip to bottom
          if (rect.top - tooltipHeight - margin < 0) {
            top = rect.bottom + scrollTop + margin;
          }
          break;
        case 'bottom':
          // Position below the element
          top = rect.bottom + scrollTop + margin;
          left = rect.left + scrollLeft + rect.width / 2;
          transform = 'translateX(-50%)';
          
          // If tooltip would go below viewport, flip to top
          if (rect.bottom + tooltipHeight + margin > window.innerHeight) {
            top = rect.top + scrollTop - tooltipHeight - margin;
          }
          break;
        case 'left':
          // Position to the left of the element
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.left + scrollLeft - tooltipWidth - margin;
          transform = 'translateY(-50%)';
          
          // If tooltip would go off-screen left, flip to right
          if (rect.left - tooltipWidth - margin < 0) {
            left = rect.right + scrollLeft + margin;
          }
          break;
        case 'right':
          // Position to the right of the element
          top = rect.top + scrollTop + rect.height / 2;
          left = rect.right + scrollLeft + margin;
          transform = 'translateY(-50%)';
          
          // If tooltip would go off-screen right, flip to left
          if (rect.right + tooltipWidth + margin > window.innerWidth) {
            left = rect.left + scrollLeft - tooltipWidth - margin;
          }
          break;
        default:
          top = rect.bottom + scrollTop + margin;
          left = rect.left + scrollLeft + rect.width / 2;
          transform = 'translateX(-50%)';
      }
      
      // Ensure tooltip stays within horizontal viewport bounds
      const tooltipLeft = left - (transform.includes('X') ? tooltipWidth / 2 : 0);
      if (tooltipLeft < margin) {
        left = margin + (transform.includes('X') ? tooltipWidth / 2 : 0);
      } else if (tooltipLeft + tooltipWidth > window.innerWidth - margin) {
        left = window.innerWidth - tooltipWidth - margin + (transform.includes('X') ? tooltipWidth / 2 : 0);
      }
      
      setTooltipPosition({ 
        top: `${Math.max(margin, top)}px`, 
        left: `${left}px`,
        transform: transform
      });
      
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (step.waitForElement) {
      // Element not found - might appear after user action
      setTargetElement(null);
    }
  }, [step]);
  
  useEffect(() => {
    if (isOpen) {
      updateTooltipPosition();
      window.addEventListener('resize', updateTooltipPosition);
      window.addEventListener('scroll', updateTooltipPosition);
      
      return () => {
        window.removeEventListener('resize', updateTooltipPosition);
        window.removeEventListener('scroll', updateTooltipPosition);
      };
    }
  }, [isOpen, currentStep, updateTooltipPosition]);
  
  // Watch for target element to appear
  useEffect(() => {
    if (!isOpen || !step.waitForElement || !step.target) return;
    
    const observer = new MutationObserver(() => {
      const element = document.querySelector(step.target);
      if (element) {
        updateTooltipPosition();
      }
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [isOpen, step, updateTooltipPosition]);
  
  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const handleComplete = () => {
    // Store completion in localStorage
    localStorage.setItem('retailer_walkthrough_completed', 'true');
    localStorage.setItem('retailer_walkthrough_completed_at', new Date().toISOString());
    
    if (onComplete) onComplete();
    onClose();
  };
  
  const handleSkip = () => {
    localStorage.setItem('retailer_walkthrough_skipped', 'true');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] transition-opacity"
        onClick={handleSkip}
      />
      
      {/* Highlight ring around target element */}
      {targetElement && step.highlight && (
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: targetElement.getBoundingClientRect().top - 4 + 'px',
            left: targetElement.getBoundingClientRect().left - 4 + 'px',
            width: targetElement.offsetWidth + 8 + 'px',
            height: targetElement.offsetHeight + 8 + 'px',
            boxShadow: '0 0 0 4px rgba(139, 92, 246, 0.6), 0 0 0 9999px rgba(0, 0, 0, 0.5)',
            borderRadius: '8px',
            animation: 'pulse 2s infinite',
          }}
        />
      )}
      
      {/* Tooltip card */}
      <Card 
        className="fixed z-[10000] w-[340px] shadow-2xl border-2 border-purple-300 bg-white"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.transform,
        }}
        data-testid="walkthrough-tooltip"
      >
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-white/20 p-2 rounded-lg">
                  <StepIcon className="w-5 h-5 text-white" />
                </div>
                <span className="text-white/80 text-sm">
                  Step {currentStep + 1} of {WALKTHROUGH_STEPS.length}
                </span>
              </div>
              <button
                onClick={handleSkip}
                className="text-white/70 hover:text-white transition-colors"
                data-testid="walkthrough-skip"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="text-white font-bold text-lg mt-3">{step.title}</h3>
          </div>
          
          {/* Content */}
          <div className="p-4">
            <p className="text-gray-600 text-sm leading-relaxed">
              {step.description}
            </p>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 my-4">
              {WALKTHROUGH_STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'bg-purple-600 w-6' 
                      : idx < currentStep 
                        ? 'bg-purple-400' 
                        : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            
            {/* Navigation buttons */}
            <div className="flex gap-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  className="flex-1"
                  data-testid="walkthrough-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                data-testid="walkthrough-next"
              >
                {isLastStep ? 'Get Started!' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
            
            {/* Skip link */}
            {!isLastStep && (
              <button
                onClick={handleSkip}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3"
              >
                Skip tutorial
              </button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.6), 0 0 0 9999px rgba(0, 0, 0, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(139, 92, 246, 0.3), 0 0 0 9999px rgba(0, 0, 0, 0.5); }
        }
      `}</style>
    </>
  );
};

export default GuidedWalkthrough;

/**
 * Hook to manage walkthrough state
 */
export const useGuidedWalkthrough = (minScansForAutoShow = 3) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Check if user should see the walkthrough
  const shouldShowWalkthrough = useCallback((totalScans = 0) => {
    const completed = localStorage.getItem('retailer_walkthrough_completed');
    const skipped = localStorage.getItem('retailer_walkthrough_skipped');
    
    // Don't show if completed or skipped, or if user has enough scans
    if (completed || skipped || totalScans >= minScansForAutoShow) {
      return false;
    }
    
    return true;
  }, [minScansForAutoShow]);
  
  const openWalkthrough = useCallback(() => setIsOpen(true), []);
  const closeWalkthrough = useCallback(() => setIsOpen(false), []);
  
  const resetWalkthrough = useCallback(() => {
    localStorage.removeItem('retailer_walkthrough_completed');
    localStorage.removeItem('retailer_walkthrough_completed_at');
    localStorage.removeItem('retailer_walkthrough_skipped');
  }, []);
  
  return {
    isOpen,
    openWalkthrough,
    closeWalkthrough,
    shouldShowWalkthrough,
    resetWalkthrough,
  };
};
